import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TutorialLibrary = ({ completedTutorials = [], onTutorialComplete, userRole }) => {
  const [selectedCategory, setSelectedCategory] = useState('getting-started');
  const [selectedTutorial, setSelectedTutorial] = useState(null);

  // Tutorial categories
  const tutorialCategories = [
    { id: 'getting-started', label: 'Premiers Pas', icon: 'Play' },
    { id: 'advanced', label: 'Avancé', icon: 'Zap' },
    { id: 'video', label: 'Tutoriels Vidéo', icon: 'Video' },
    { id: 'interactive', label: 'Interactif', icon: 'MousePointer' }
  ];

  // Tutorial data
  const tutorials = {
    'getting-started': [
      {
        id: 'setup-profile',
        title: 'Configuration de votre profil',
        description: 'Apprenez à configurer votre profil pour maximiser votre visibilité',
        duration: '5 min',
        type: 'interactive',
        difficulty: 'Débutant',
        steps: [
          'Accéder aux paramètres de profil',
          'Compléter les informations personnelles',
          'Ajouter une photo de profil professionnelle',
          'Configurer vos préférences de notification',
          'Valider et publier votre profil'
        ]
      },
      {
        id: 'first-campaign',
        title: 'Créer votre première campagne',
        description: 'Guide étape par étape pour lancer votre première campagne marketing',
        duration: '10 min',
        type: 'step-by-step',
        difficulty: 'Débutant',
        steps: [
          'Accéder au générateur de campagnes',
          'Définir vos objectifs marketing',
          'Choisir votre audience cible',
          'Configurer les paramètres de suivi',
          'Lancer et monitorer la campagne'
        ]
      },
      {
        id: 'understand-dashboard',
        title: 'Naviguer dans le tableau de bord',
        description: 'Découvrez toutes les fonctionnalités de votre tableau de bord personnalisé',
        duration: '8 min',
        type: 'guided-tour',
        difficulty: 'Débutant',
        steps: [
          'Vue d\'ensemble des métriques principales',
          'Navigation dans les sections',
          'Personnaliser l\'affichage',
          'Configurer les raccourcis',
          'Utiliser les filtres et recherches'
        ]
      }
    ],
    'advanced': [
      {
        id: 'advanced-analytics',
        title: 'Analytics avancées et reporting',
        description: 'Maîtrisez les outils d\'analyse pour optimiser vos performances',
        duration: '15 min',
        type: 'interactive',
        difficulty: 'Avancé',
        steps: [
          'Interpréter les métriques complexes',
          'Créer des rapports personnalisés',
          'Utiliser les segments d\'audience',
          'Configurer les alertes automatiques',
          'Exporter et partager les données'
        ]
      },
      {
        id: 'automation-setup',
        title: 'Configuration des automatisations',
        description: 'Automatisez vos processus pour gagner en efficacité',
        duration: '20 min',
        type: 'step-by-step',
        difficulty: 'Avancé',
        steps: [
          'Identifier les processus automatisables',
          'Configurer les règles d\'automatisation',
          'Paramétrer les conditions de déclenchement',
          'Tester et valider les automatisations',
          'Surveiller les performances automatisées'
        ]
      },
      {
        id: 'api-integration',
        title: 'Intégration API et webhooks',
        description: 'Intégrez Artis.io avec vos systèmes existants via notre API',
        duration: '25 min',
        type: 'technical',
        difficulty: 'Expert',
        steps: [
          'Obtenir vos clés API',
          'Comprendre la structure des endpoints',
          'Implémenter l\'authentification',
          'Configurer les webhooks',
          'Gérer les erreurs et la surveillance'
        ]
      }
    ],
    'video': [
      {
        id: 'platform-overview',
        title: 'Vue d\'ensemble de la plateforme',
        description: 'Découverte complète d\'Artis.io en vidéo',
        duration: '12 min',
        type: 'video',
        difficulty: 'Tous niveaux',
        videoUrl: '#',
        thumbnail: '/api/placeholder/400/225'
      },
      {
        id: 'success-stories',
        title: 'Témoignages et cas d\'usage',
        description: 'Découvrez comment nos utilisateurs réussissent avec Artis.io',
        duration: '18 min',
        type: 'video',
        difficulty: 'Tous niveaux',
        videoUrl: '#',
        thumbnail: '/api/placeholder/400/225'
      },
      {
        id: 'live-demo',
        title: 'Démonstration en direct',
        description: 'Session de démonstration avec un expert Artis.io',
        duration: '35 min',
        type: 'video',
        difficulty: 'Tous niveaux',
        videoUrl: '#',
        thumbnail: '/api/placeholder/400/225'
      }
    ],
    'interactive': [
      {
        id: 'guided-setup',
        title: 'Configuration guidée interactive',
        description: 'Configurez votre compte pas à pas avec assistance',
        duration: '15 min',
        type: 'interactive',
        difficulty: 'Débutant'
      },
      {
        id: 'scenario-simulation',
        title: 'Simulation de scénarios',
        description: 'Pratiquez avec des scénarios réels dans un environnement sécurisé',
        duration: '30 min',
        type: 'interactive',
        difficulty: 'Intermédiaire'
      }
    ]
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Débutant':
        return 'text-success bg-success/10';
      case 'Intermédiaire':
        return 'text-warning bg-warning/10';
      case 'Avancé':
        return 'text-error bg-error/10';
      case 'Expert':
        return 'text-purple-500 bg-purple-100';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return 'Video';
      case 'interactive':
        return 'MousePointer';
      case 'step-by-step':
        return 'List';
      case 'guided-tour':
        return 'Navigation';
      case 'technical':
        return 'Code';
      default:
        return 'BookOpen';
    }
  };

  const isTutorialCompleted = (tutorialId) => {
    return completedTutorials?.includes(tutorialId);
  };

  const handleStartTutorial = (tutorial) => {
    setSelectedTutorial(tutorial);
  };

  const handleCompleteTutorial = (tutorialId) => {
    onTutorialComplete(tutorialId);
    setSelectedTutorial(null);
  };

  const currentTutorials = tutorials?.[selectedCategory] || [];

  const renderTutorialModal = () => {
    if (!selectedTutorial) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">{selectedTutorial?.title}</h2>
              <p className="text-sm text-muted-foreground">{selectedTutorial?.description}</p>
            </div>
            <button
              onClick={() => setSelectedTutorial(null)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {selectedTutorial?.type === 'video' ? (
              <div className="space-y-4">
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Play" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Lecteur vidéo - {selectedTutorial?.duration}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-card-foreground">Étapes du tutoriel :</h3>
                <div className="space-y-3">
                  {selectedTutorial?.steps?.map((step, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center text-xs text-accent-foreground font-medium flex-shrink-0 mt-0.5">
                        {index + 1}
                      </div>
                      <p className="text-muted-foreground">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button variant="outline" onClick={() => setSelectedTutorial(null)}>
              Fermer
            </Button>
            <Button onClick={() => handleCompleteTutorial(selectedTutorial?.id)}>
              <Icon name="Check" size={16} className="mr-2" />
              Marquer comme terminé
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="PlayCircle" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-card-foreground">Bibliothèque de Tutoriels</h2>
            <p className="text-muted-foreground">Apprenez à votre rythme avec nos tutoriels interactifs</p>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="mb-8 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-card-foreground">Votre Progression</h3>
            <div className="text-sm text-muted-foreground">
              {completedTutorials?.length || 0} / {Object.values(tutorials)?.flat()?.length} terminés
            </div>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-accent h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${((completedTutorials?.length || 0) / Object.values(tutorials)?.flat()?.length) * 100}%` 
              }}
            />
          </div>
        </div>

        {/* Category Selector */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {tutorialCategories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category?.id
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={category?.icon} size={16} />
                <span>{category?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tutorial Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentTutorials?.map((tutorial) => {
            const isCompleted = isTutorialCompleted(tutorial?.id);
            
            return (
              <div key={tutorial?.id} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                {tutorial?.thumbnail ? (
                  <div className="aspect-video bg-muted relative">
                    <img 
                      src={tutorial?.thumbnail} 
                      alt={tutorial?.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <Icon name="Play" size={32} className="text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="h-32 bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center">
                    <Icon name={getTypeIcon(tutorial?.type)} size={32} className="text-accent" />
                  </div>
                )}
                
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(tutorial?.difficulty)}`}>
                        {tutorial?.difficulty}
                      </span>
                      {isCompleted && (
                        <Icon name="CheckCircle" size={16} className="text-success" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{tutorial?.duration}</span>
                  </div>
                  
                  <h3 className="font-semibold text-card-foreground mb-2">{tutorial?.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{tutorial?.description}</p>
                  
                  <Button
                    variant={isCompleted ? "outline" : "default"}
                    className="w-full"
                    onClick={() => handleStartTutorial(tutorial)}
                  >
                    <Icon 
                      name={isCompleted ? "RefreshCw" : "Play"} 
                      size={16} 
                      className="mr-2" 
                    />
                    {isCompleted ? "Revoir" : "Commencer"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Achievement Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Award" size={20} className="text-warning" />
          <h3 className="text-lg font-semibold text-card-foreground">Récompenses et Badges</h3>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`text-center p-4 rounded-lg ${completedTutorials?.length >= 3 ? 'bg-success/10' : 'bg-muted/50'}`}>
            <Icon name="Star" size={32} className={`mx-auto mb-2 ${completedTutorials?.length >= 3 ? 'text-success' : 'text-muted-foreground'}`} />
            <div className="text-sm font-medium">Premier Pas</div>
            <div className="text-xs text-muted-foreground">3 tutoriels</div>
          </div>
          
          <div className={`text-center p-4 rounded-lg ${completedTutorials?.length >= 6 ? 'bg-warning/10' : 'bg-muted/50'}`}>
            <Icon name="Zap" size={32} className={`mx-auto mb-2 ${completedTutorials?.length >= 6 ? 'text-warning' : 'text-muted-foreground'}`} />
            <div className="text-sm font-medium">Apprenant Actif</div>
            <div className="text-xs text-muted-foreground">6 tutoriels</div>
          </div>
          
          <div className={`text-center p-4 rounded-lg ${completedTutorials?.length >= 10 ? 'bg-accent/10' : 'bg-muted/50'}`}>
            <Icon name="Target" size={32} className={`mx-auto mb-2 ${completedTutorials?.length >= 10 ? 'text-accent' : 'text-muted-foreground'}`} />
            <div className="text-sm font-medium">Expert</div>
            <div className="text-xs text-muted-foreground">10 tutoriels</div>
          </div>
          
          <div className={`text-center p-4 rounded-lg ${completedTutorials?.length >= 15 ? 'bg-error/10' : 'bg-muted/50'}`}>
            <Icon name="Crown" size={32} className={`mx-auto mb-2 ${completedTutorials?.length >= 15 ? 'text-error' : 'text-muted-foreground'}`} />
            <div className="text-sm font-medium">Maître</div>
            <div className="text-xs text-muted-foreground">15 tutoriels</div>
          </div>
        </div>
      </div>
      {renderTutorialModal()}
    </div>
  );
};

export default TutorialLibrary;